import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Accessibility, X, Type, Eye, Volume2, VolumeX, Minus, Plus,
  Sparkles, Link as LinkIcon, RotateCcw, Brain,
} from 'lucide-react';
import { useAccessibility } from '../../context/AccessibilityContext';
import { useLanguage } from '../../context/LanguageContext';
import { useTTS } from '../../hooks/useTTS';

/**
 * AccessibilityPanel — floating FAB + full-height side panel.
 * ────────────────────────────────────────────────────────────────
 * Mirrors the Trust Design accessibility toolkit so users moving
 * between LWT and TD see the same controls. Everything is fully
 * localised: copy comes through `localize({ar, en})` and supports
 * all 7 languages in `LanguageContext`. Read-aloud (TTS) uses the
 * user's current UI language by default.
 *
 * Quiet Mode is the differentiator for our autism path — it stops
 * every animation, hides decorative blobs, and flattens gradients.
 */
const COLOR_BLIND_OPTIONS = [
  { v: 'off',           ar: 'إيقاف',                     en: 'Off' },
  { v: 'deuteranopia',  ar: 'عمى الألوان الأخضر',         en: 'Deuteranopia' },
  { v: 'protanopia',    ar: 'عمى الألوان الأحمر',         en: 'Protanopia' },
  { v: 'tritanopia',    ar: 'عمى الألوان الأزرق-الأصفر',  en: 'Tritanopia' },
];

const FONT_SCALE_STEPS = [0.85, 1, 1.15, 1.3];

export default function AccessibilityPanel() {
  const [open, setOpen] = useState(false);
  const a11y = useAccessibility();
  const { language, localize } = useLanguage();
  const { speak, supported: ttsSupported } = useTTS();
  const isAr = language === 'ar';
  const t = (ar, en) => localize({ ar, en });

  const bumpFont = (dir) => {
    const idx = FONT_SCALE_STEPS.indexOf(a11y.fontScale);
    const next = Math.max(0, Math.min(FONT_SCALE_STEPS.length - 1, idx + dir));
    a11y.set({ fontScale: FONT_SCALE_STEPS[next] });
  };

  return (
    <>
      <button
        type="button"
        className="a11y-fab"
        onClick={() => setOpen(true)}
        aria-label={t('فتح خيارات إمكانية الوصول', 'Open accessibility menu')}
        data-testid="a11y-fab"
      >
        <Accessibility className="w-6 h-6" strokeWidth={2.2} />
      </button>

      <AnimatePresence>
        {open && (
          <>
            <motion.div
              className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[9990]"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
              aria-hidden
            />
            <motion.aside
              className={`a11y-panel fixed top-0 bottom-0 ${isAr ? 'right-0' : 'left-0'} w-full max-w-sm bg-white dark:bg-slate-900 shadow-2xl ring-1 ring-black/5 overflow-y-auto`}
              initial={{ x: isAr ? '100%' : '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: isAr ? '100%' : '-100%' }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              role="dialog"
              aria-label={t('خيارات إمكانية الوصول', 'Accessibility settings')}
              data-testid="a11y-panel"
            >
              {/* Header */}
              <header className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-white/10 sticky top-0 bg-white dark:bg-slate-900 z-10">
                <div className="flex items-center gap-2">
                  <Accessibility className="w-5 h-5 text-indigo-600" />
                  <h2 className="text-base font-bold text-slate-900 dark:text-white">
                    {t('إمكانية الوصول', 'Accessibility')}
                  </h2>
                </div>
                <button
                  onClick={() => setOpen(false)}
                  aria-label={t('إغلاق', 'Close')}
                  className="w-8 h-8 rounded-full hover:bg-slate-100 dark:hover:bg-white/10 flex items-center justify-center text-slate-600 dark:text-slate-300"
                  data-testid="a11y-panel-close"
                >
                  <X className="w-4 h-4" />
                </button>
              </header>

              <div className="p-5 space-y-5">

                {/* 1. Dyslexia font */}
                <ToggleRow
                  icon={Type}
                  title={t('وضع عسر القراءة', 'Dyslexia mode')}
                  desc={t('خط OpenDyslexic مع تباعد مريح', 'OpenDyslexic font with relaxed spacing')}
                  on={a11y.dyslexiaFont}
                  onChange={(v) => a11y.set({ dyslexiaFont: v })}
                  testId="a11y-toggle-dyslexia"
                />

                {/* 2. High contrast */}
                <ToggleRow
                  icon={Eye}
                  title={t('تباين عالٍ', 'High contrast')}
                  desc={t('أبيض على أسود ولون ذهبي للروابط', 'Black/white with yellow links')}
                  on={a11y.highContrast}
                  onChange={(v) => a11y.set({ highContrast: v })}
                  testId="a11y-toggle-contrast"
                />

                {/* 3. Underline links */}
                <ToggleRow
                  icon={LinkIcon}
                  title={t('تمييز الروابط', 'Underline links')}
                  desc={t('خط تحت كل رابط لسهولة التمييز', 'Underline every link for clarity')}
                  on={a11y.underlineLinks}
                  onChange={(v) => a11y.set({ underlineLinks: v })}
                  testId="a11y-toggle-links"
                />

                {/* 4. Quiet mode (autism path) */}
                <ToggleRow
                  icon={Brain}
                  title={t('النمط الهادئ', 'Quiet mode')}
                  desc={t('بدون حركات أو مؤثرات — مثالي للطلاب من طيف التوحد', 'No motion · no decorations · designed for autism spectrum users')}
                  on={a11y.quietMode}
                  onChange={(v) => a11y.set({ quietMode: v })}
                  accent="emerald"
                  testId="a11y-toggle-quiet"
                />

                {/* 5. Font scale stepper */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-indigo-500" />
                      <span className="text-sm font-bold text-slate-900 dark:text-white">
                        {t('حجم الخط', 'Text size')}
                      </span>
                    </div>
                    <span className="text-xs text-slate-500" data-testid="a11y-fontscale-value">
                      {Math.round(a11y.fontScale * 100)}%
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => bumpFont(-1)}
                      className="flex-1 h-10 rounded-lg bg-slate-100 hover:bg-slate-200 dark:bg-white/5 dark:hover:bg-white/10 flex items-center justify-center text-slate-700 dark:text-slate-200"
                      aria-label={t('تصغير الخط', 'Decrease font size')}
                      data-testid="a11y-fontscale-down"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => a11y.set({ fontScale: 1 })}
                      className="flex-1 h-10 rounded-lg bg-slate-100 hover:bg-slate-200 dark:bg-white/5 dark:hover:bg-white/10 text-xs font-bold text-slate-700 dark:text-slate-200"
                      data-testid="a11y-fontscale-reset"
                    >
                      A
                    </button>
                    <button
                      onClick={() => bumpFont(+1)}
                      className="flex-1 h-10 rounded-lg bg-slate-100 hover:bg-slate-200 dark:bg-white/5 dark:hover:bg-white/10 flex items-center justify-center text-slate-700 dark:text-slate-200"
                      aria-label={t('تكبير الخط', 'Increase font size')}
                      data-testid="a11y-fontscale-up"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* 6. Color-blind selector */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Eye className="w-4 h-4 text-indigo-500" />
                    <span className="text-sm font-bold text-slate-900 dark:text-white">
                      {t('عمى الألوان', 'Color blindness')}
                    </span>
                  </div>
                  <select
                    value={a11y.colorBlindMode}
                    onChange={(e) => a11y.set({ colorBlindMode: e.target.value })}
                    className="w-full h-10 rounded-lg border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm px-3"
                    data-testid="a11y-colorblind-select"
                  >
                    {COLOR_BLIND_OPTIONS.map((o) => (
                      <option key={o.v} value={o.v}>{t(o.ar, o.en)}</option>
                    ))}
                  </select>
                </div>

                {/* 7. Read aloud (TTS) */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    {a11y.readAloud ? <Volume2 className="w-4 h-4 text-emerald-500" /> : <VolumeX className="w-4 h-4 text-slate-400" />}
                    <span className="text-sm font-bold text-slate-900 dark:text-white">
                      {t('القارئ الصوتي', 'Read aloud')}
                    </span>
                    {!ttsSupported && (
                      <span className="text-[10px] text-amber-600 font-semibold">
                        {t('متصفحك لا يدعم', 'Browser unsupported')}
                      </span>
                    )}
                  </div>
                  <p className="text-[12px] text-slate-500 dark:text-slate-400">
                    {t('استمع لأي فقرة بنقرة واحدة', 'Listen to any paragraph with one click')}
                  </p>
                  <div className="flex gap-2">
                    <button
                      disabled={!ttsSupported}
                      onClick={() => a11y.set({ readAloud: !a11y.readAloud })}
                      className={`flex-1 h-10 rounded-lg text-sm font-bold transition-colors ${
                        a11y.readAloud
                          ? 'bg-emerald-500 text-white hover:bg-emerald-600'
                          : 'bg-slate-100 dark:bg-white/5 text-slate-700 dark:text-slate-200 hover:bg-slate-200'
                      } ${!ttsSupported ? 'opacity-50 cursor-not-allowed' : ''}`}
                      data-testid="a11y-tts-toggle"
                    >
                      {a11y.readAloud ? t('مُفعَّل', 'Enabled') : t('معطَّل', 'Disabled')}
                    </button>
                    <button
                      disabled={!ttsSupported}
                      onClick={() => speak(t('مرحباً بك في تعلّم بثقة', 'Welcome to Learn with Trust'), { lang: language })}
                      className="px-4 h-10 rounded-lg bg-indigo-500 hover:bg-indigo-600 text-white text-xs font-bold disabled:opacity-50"
                      data-testid="a11y-tts-test"
                    >
                      {t('اختبار', 'Test')}
                    </button>
                  </div>
                </div>

                {/* Reset */}
                <button
                  onClick={a11y.reset}
                  className="w-full h-10 rounded-lg ring-1 ring-rose-200 dark:ring-rose-400/30 text-rose-600 dark:text-rose-300 text-sm font-bold flex items-center justify-center gap-2 hover:bg-rose-50 dark:hover:bg-rose-500/10"
                  data-testid="a11y-reset"
                >
                  <RotateCcw className="w-4 h-4" />
                  {t('إعادة إلى الإعدادات الافتراضية', 'Reset to defaults')}
                </button>

                <p className="text-[11px] text-center text-slate-400 pt-2 border-t border-slate-100 dark:border-white/5">
                  {t('تُحفَظ تفضيلاتك تلقائياً في هذا المتصفح', 'Your preferences are saved locally on this device')}
                </p>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
}

/* ── Reusable toggle row ─────────────────────────────────────── */
function ToggleRow({ icon: Icon, title, desc, on, onChange, accent = 'indigo', testId }) {
  const trackOn = accent === 'emerald' ? 'bg-emerald-500' : 'bg-indigo-500';
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="flex items-start gap-3 flex-1 min-w-0">
        <Icon className="w-5 h-5 mt-0.5 shrink-0 text-indigo-500" />
        <div className="min-w-0">
          <p className="text-sm font-bold text-slate-900 dark:text-white">{title}</p>
          <p className="text-[12px] text-slate-500 dark:text-slate-400 mt-0.5">{desc}</p>
        </div>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        onClick={() => onChange(!on)}
        data-testid={testId}
        className={`relative shrink-0 w-11 h-6 rounded-full transition-colors ${
          on ? trackOn : 'bg-slate-300 dark:bg-white/15'
        }`}
      >
        <span
          className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
            on ? 'translate-x-5 rtl:-translate-x-5' : 'translate-x-0.5'
          }`}
        />
      </button>
    </div>
  );
}
